import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Store token in memory (React state) instead of localStorage for security
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Configure axios to include credentials (cookies) in requests
  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  // Configure axios defaults for authorization
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Clear any scheduled refresh
  const clearRefreshTimer = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  // Refresh access token using HttpOnly cookie
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`, {}, {
        withCredentials: true
      });
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      return { success: true, access_token, user: userData };
    } catch {
      // Refresh failed - clear state
      setToken(null);
      setUser(null);
      clearRefreshTimer();
      return { success: false };
    }
  }, [clearRefreshTimer]);

  // Schedule token refresh (refresh 1 minute before expiry)
  const scheduleRefresh = useCallback((tokenExpireMinutes = 15) => {
    clearRefreshTimer();
    // Refresh 1 minute before the token expires
    const refreshTime = (tokenExpireMinutes - 1) * 60 * 1000;
    refreshTimeoutRef.current = setTimeout(async () => {
      const result = await refreshAccessToken();
      if (result.success) {
        // Schedule next refresh
        scheduleRefresh(tokenExpireMinutes);
      }
    }, refreshTime);
  }, [clearRefreshTimer, refreshAccessToken]);

  // Try to restore session on mount using refresh token cookie
  useEffect(() => {
    const initAuth = async () => {
      try {
        const result = await refreshAccessToken();
        if (result.success) {
          scheduleRefresh();
        }
      } catch (error) {
        // Auth init failed - user will need to login
        console.warn('Auth initialization failed:', error.message);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => clearRefreshTimer();
  }, [refreshAccessToken, scheduleRefresh, clearRefreshTimer]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);

      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true
      });

      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      scheduleRefresh();
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed";
      setError(message);
      return { success: false, error: message };
    }
  }, [scheduleRefresh]);

  const register = useCallback(async (email, password, fullName) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName,
      }, {
        withCredentials: true
      });

      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      scheduleRefresh();
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || "Registration failed";
      setError(message);
      return { success: false, error: message };
    }
  }, [scheduleRefresh]);

  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to clear HttpOnly cookie
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Even if the API call fails, clear local state
      console.error("Logout API call failed");
    }
    
    setToken(null);
    setUser(null);
    clearRefreshTimer();
    delete axios.defaults.headers.common["Authorization"];
  }, [clearRefreshTimer]);

  const isAdmin = user?.role === "admin";
  const isAuthenticated = !!user;
  
  // Discount limits by level
  const discountLimits = {
    standard: 10,
    manager: 15,
    vp: 20,
    admin: 100,
  };
  const discountLevel = user?.discount_level || 'standard';
  const maxDiscountPercent = discountLimits[discountLevel] || 10;

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    token,
    loading,
    error,
    isAdmin,
    isAuthenticated,
    discountLevel,
    maxDiscountPercent,
    login,
    register,
    logout,
    setError,
    refreshAccessToken,
  }), [user, token, loading, error, isAdmin, isAuthenticated, discountLevel, maxDiscountPercent, login, register, logout, refreshAccessToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
